import React, { useContext } from 'react'
import { Modal, Form, Input, InputNumber, Button, Row, Col, Space } from 'antd'
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons'
import { AppContext } from 'AppContext'
import { canUserEdit, canUserAdd, canUserSave } from 'utils/User'

const CollectionCreateForm = ({
  visible,
  onCreate,
  onUpdate,
  onCancel,
  isEdit,
  selectedPlan,
}) => {
  const { state: { currentUser, userPermissions, isClient } = {} } = useContext(AppContext) || {}
  const hasEdit = canUserEdit(userPermissions || currentUser, isClient)
  const hasAdd = canUserAdd(userPermissions || currentUser, isClient)
  const hasSave = canUserSave(userPermissions || currentUser, isClient)
  const canSubmit = isEdit ? (hasEdit && hasSave) : (hasAdd && hasSave)

  const [form] = Form.useForm()

  function resetForm() {
    form.resetFields()
  }

  return (
    <Modal
      maskClosable={false}
      visible={visible}
      title={isEdit ? 'Edit Plan' : 'Add New Plan'}
      okText={isEdit ? 'Update Plan' : 'Add Plan'}
      okButtonProps={{ disabled: !canSubmit }}
      cancelText='Cancel'
      onCancel={() => {
        resetForm()
        onCancel()
      }}
      onOk={() => {
        if (!canSubmit) return
        form
          .validateFields()
          .then((values) => {
            const data = {
              ...values,
            }
            if (isEdit) {
              onUpdate(data, resetForm)
            } else {
              onCreate(data, resetForm)
            }
          })
          .catch((info) => console.log('Validate Failed:', info))
      }}
    >
      <Form
        form={form}
        layout="vertical"
        className='client-detail-form'
        initialValues={{
          modifier: 'public',
          ...selectedPlan,
        }}
      >
        <Row gutter={16} className='client-profile-row'>
          <Col
            xs={24}
          >
            <Form.Item
              name="name"
              label="Plan Name"
              rules={[{ required: true, message: 'Please input your plan name!' }]}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col xs={24}>
            <Form.Item
              name="description"
              label="Plan Description"
              rules={[{ required: true, message: 'Please input your description!' }]}
            >
              <Input.TextArea rows={3} />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name="strategyCode"
              label="Strategy Code"
              rules={[
                { required: true, message: 'Please input the Strategy Code!' },
                { pattern: /^\d+$/, message: 'Strategy Code must contain only numbers!' }
              ]}
            >
              <Input
                onKeyPress={(e) => {
                  if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight' && e.key !== 'Tab') {
                    e.preventDefault()
                  }
                }}
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name="minPoints"
              label="Min Points"
              rules={[{ required: true, message: 'Please input your Min Points!' }]}
            >
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name="maxPoints"
              label="Max Points"
              rules={[{ required: true, message: 'Please input your Max Points!' }]}
            >
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name="percentage"
              label="Percentage"
              rules={[{ required: true, message: 'Please input your Percentage!' }]}
            >
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          </Col>

          <Col xs={24}>
            <Form.Item label="AMC Percentage Range Brackets">
              <Form.List name="amcPercentage">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, fieldKey, ...restField }) => (
                      <Space
                        key={key}
                        style={{
                          display: 'flex',
                          marginBottom: 8,
                          alignItems: 'baseline',
                        }}
                      >
                        <Form.Item
                          {...restField}
                          name={[name, 'minExposure']}
                          fieldKey={[fieldKey, 'minExposure']}
                          rules={[{ required: true, message: 'Missing min exposure' }]}
                        >
                          <InputNumber style={{ width: '150px' }} placeholder="Min Exposure" min={0} />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[name, 'maxExposure']}
                          fieldKey={[fieldKey, 'maxExposure']}
                        >
                          <InputNumber style={{ width: '150px' }} placeholder="Max Exposure" min={0} />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[name, 'percentage']}
                          fieldKey={[fieldKey, 'percentage']}
                          rules={[{ required: true, message: 'Missing percentage' }]}
                        >
                          <InputNumber style={{ width: '120px' }} step={0.01} min={0} max={100} placeholder="Percentage" />
                        </Form.Item>
                        <MinusCircleOutlined
                          style={{ fontSize: '16px', color: '#ff4d4f', cursor: 'pointer' }}
                          onClick={() => remove(name)}
                        />
                      </Space>
                    ))}
                    <Form.Item>
                      <Button
                        type="dashed"
                        onClick={() => add()}
                        icon={<PlusOutlined />}
                        style={{ width: '100%' }}
                      >
                        Add AMC Range Bracket
                      </Button>
                    </Form.Item>
                  </>
                )}
              </Form.List>
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name="stock"
              label="Stock"
              rules={[{ required: true, message: 'Please input your Stock!' }]}
            >
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name="sip"
              label="Sip"
              rules={[{ required: true, message: 'Please input your Sip!' }]}
            >
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name="pnlOn"
              label="Pnl On"
              rules={[{ required: true, message: 'Please input your Pnl On!' }]}
            >
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name="interestFromDebt"
              label="Interest From Debt"
              rules={[{ required: true, message: 'Please input your interest From Debt!' }]}
            >
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name="features"
              label="Features"
              rules={[{ required: true, message: 'Please input your Features!' }]}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name="planRiskType"
              label="Plan Risk Type"
              rules={[{ required: true, message: 'Please input your Plan Risk Type!' }]}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name="minRisk"
              label="Min Risk"
              rules={[{ required: true, message: 'Please input your Min Risk!' }]}
            >
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name="maxRisk"
              label="Max Risk"
              rules={[{ required: true, message: 'Please input your Max Risk!' }]}
            >
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name="tCost"
              label="T Cost"
              rules={[{ required: true, message: 'Please input your T Cost!' }]}
            >
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          </Col>

          <Col xs={24}>
            <Form.Item
              name="planRisk"
              label="Plan Risk"
              rules={[{ required: true, message: 'Please input your Plan Risk!' }]}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col xs={24}>
            <Form.Item
              name="riskAppetite"
              label="Risk Appetite"
              rules={[{ required: true, message: 'Please input risk appetite!' }]}
            >
              <Input />
            </Form.Item>
          </Col>

        </Row>

        <div className="footer-btn-div">
          <Button
            className="footer-cancel-btn"
            onClick={() => {
              resetForm()
              onCancel()
            }}
          >
            Cancel
          </Button>

          <Button
            className="footer-submit-btn"
            type="primary"
            disabled={!canSubmit}
            onClick={() => {
              if (!canSubmit) return
              form.validateFields().then((values) => {
                isEdit ? onUpdate(values, resetForm) : onCreate(values, resetForm)
              })
            }}
          >
            {isEdit ? 'Update Plan' : 'Add Plan'}
          </Button>
        </div>
      </Form>

    </Modal>
  )
}

const CreatePlanModal = (props) => {
  return (
    <div>
      <CollectionCreateForm {...props} />
    </div>
  )
}

export default CreatePlanModal
